import { ReactNode, createContext, useContext, useState } from 'react'

type ModalData = {
  isOpen: boolean
  [key: string]: any
}

type ModalsState = {
  [modalId: string]: ModalData
}

type ModalContextType = {
  modals: ModalsState
  openModal: (modalId: string, props?: Record<string, any>) => void
  closeModal: (modalId: string) => void
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

type ModalProviderProps = {
  children: ReactNode
}

export const ModalProvider = ({ children }: ModalProviderProps) => {
  // State to track all modals and their properties
  // modals = { lidarModal: { isOpen: true, onSuccess: fn }, gisModal: { isOpen: false } }
  const [modals, setModals] = useState<ModalsState>({})

  /**
   * Opens a modal with the given ID and optional props
   *
   * @param modalId - Unique identifier for the modal (e.g., 'lidarModal', 'gisModal')
   * @param props - Optional props to pass to the modal component
   *
   * Example: openModal('lidarModal', { onSuccess: handleSuccess, userId: 123 })
   */
  const openModal = (modalId: string, props: Record<string, any> = {}) => {
    setModals(prev => ({
      ...prev, // keeps all existing modals unchanged
      [modalId]: {
        // computed property name: uses modalId as the key
        isOpen: true,
        ...props, // spread any additional props passed to the modal
      },
    }))
  }

  /**
   * Closes a modal with the given ID
   *
   * @param modalId - Unique identifier for the modal to close
   *
   * This sets isOpen to false but keeps the modal in state (for cleanup purposes)
   */
  const closeModal = (modalId: string) => {
    setModals(prev => ({
      ...prev, // keep all other modals unchanged
      [modalId]: {
        // target the specific modal
        ...prev[modalId], // keep existing props (like onSuccess callbacks)
        isOpen: false, // only change the isOpen state
      },
    }))
  }

  return (
    <ModalContext.Provider value={{ modals, openModal, closeModal }}>
      {children}
    </ModalContext.Provider>
  )
}

/**
 * Custom hook to access modal context
 *
 * This hook provides a clean way to access modal functions from any component.
 * It also includes error handling to ensure it's used within a ModalProvider.
 *
 * @returns {ModalContextType} - Object containing modals state and management functions
 *
 * Usage:
 * const { openModal, closeModal, modals } = useModal()
 * openModal('lidarModal', { onSuccess: handleSuccess })
 */
export const useModal = (): ModalContextType => {
  const context = useContext(ModalContext)

  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider')
  }

  return context
}

export { ModalContext }
